/**
 * Générateur — Attendu 959 — Savoir que les hauteurs d'un triangle sont
 * concourantes (orthocentre).
 *
 * Niveau : 5ème | Thème #10 Triangles (O18) | Domaine : Espace et géométrie
 * Source prof : cluster 4, Créé n°37, extraction exerciseur/triangles.
 *
 * 4 formats / 2 cartes :
 *  - orthocentre : nom du point de concours (QCM, distracteurs = les 2 autres
 *    centres), position selon le type de triangle (aigu → intérieur, rectangle
 *    → sommet de l'angle droit, obtus → extérieur), et V/F de propriétés.
 *  - tracer : les trois hauteurs d'un triangle aigu (programme_construction).
 */

import {
    randInt, randomChoice, shuffle,
    makeQcm, makeVraiFaux, makeProgrammeConstruction,
} from '../../../utils';
import { pied } from './svgTriangles';

const ATTENDU_ID = 959;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_ORTHO = 'L\'orthocentre';
const L_TRACER = 'Tracer les trois hauteurs';

const PX = 30;
const VBC = { w: 600, h: 400 };
const COL_V = '#1D9E75';
const pick = (n) => shuffle('ABCDEFGHJKLMNPRSTUVWXYZ'.split('')).slice(0, n);

// ═══════════════════════════════════════════════════════════
// Carte 1 — L'orthocentre (Créé n°37)
// ═══════════════════════════════════════════════════════════

const CHOIX_NOM = [
    'l\'orthocentre',
    'le centre de gravité',
    'le centre du cercle circonscrit',
    'le centre du cercle inscrit',
];

const genPointQcm = () => {
    const [nA, nB, nC] = pick(3);
    const texte = `<p>Dans le triangle ${nA}${nB}${nC}, comment appelle-t-on le point de concours `
        + 'des trois hauteurs ?</p>';
    const payload = makeQcm(CHOIX_NOM, CHOIX_NOM[0], texte, { ...opts(L_ORTHO) });
    payload._meta.generatorKey = 'point_qcm_unique';
    return payload;
};

const CHOIX_POS = [
    'à l\'intérieur du triangle',
    'à l\'extérieur du triangle',
    'au sommet de l\'angle droit',
    'sur un côté du triangle',
];

const genPositionQcm = () => {
    const [nA, nB, nC] = pick(3);
    const type = randomChoice(['aigu', 'rectangle', 'obtus']);
    let desc, correct;
    if (type === 'aigu') {
        desc = `a tous ses angles aigus`;
        correct = CHOIX_POS[0];
    } else if (type === 'rectangle') {
        desc = `est rectangle en ${nA}`;
        correct = CHOIX_POS[2];
    } else {
        desc = `a un angle obtus en ${nA}`;
        correct = CHOIX_POS[1];
    }
    const texte = `<p>Le triangle ${nA}${nB}${nC} ${desc}. Où se situe son orthocentre `
        + '(le point de concours des trois hauteurs) ?</p>';
    const payload = makeQcm(CHOIX_POS, correct, texte, { ...opts(L_ORTHO) });
    payload._meta.generatorKey = 'position_qcm_unique';
    return payload;
};

const VF_VRAIS = [
    'Les trois hauteurs d\'un triangle sont toujours concourantes.',
    'Le point de concours des trois hauteurs s\'appelle l\'orthocentre.',
    'Dans un triangle rectangle, l\'orthocentre est le sommet de l\'angle droit.',
    'Dans un triangle obtusangle, l\'orthocentre est situé à l\'extérieur du triangle.',
];
const VF_FAUX = [
    'L\'orthocentre est toujours situé à l\'intérieur du triangle.',
    'L\'orthocentre est le point de concours des trois médianes.',
    'L\'orthocentre est le centre du cercle circonscrit au triangle.',
];

const genProprietesVf = () => {
    const vrais = shuffle(VF_VRAIS).slice(0, 3).map((t) => ({ t, v: 0 }));
    const faux = shuffle(VF_FAUX).slice(0, 2).map((t) => ({ t, v: 1 }));
    const mixed = shuffle([...vrais, ...faux]);
    const texte = '<p>Pour chaque affirmation sur l\'orthocentre, indique si elle est vraie ou fausse.</p>';
    const payload = makeVraiFaux(mixed.map((x) => x.t), mixed.map((x) => x.v), texte, {
        ...opts(L_ORTHO),
    });
    payload._meta.generatorKey = 'proprietes_tableau_vf';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Tracer les trois hauteurs (triangle aigu)
// ═══════════════════════════════════════════════════════════

const genTracerPC = () => {
    const [nA, nB, nC] = pick(3);
    const By = 330;
    const Bx = randInt(140, 175);
    const Cx = randInt(415, 460);
    const A = { x: randInt(Bx + 90, Cx - 90), y: randInt(120, 165) };
    const B = { x: Bx, y: By };
    const C = { x: Cx, y: By };
    const hA = pied(A, B, C);
    const hB = pied(B, A, C);
    const hC = pied(C, A, B);
    const rnd = (p) => ({ x: Math.round(p.x), y: Math.round(p.y) });
    const texte = `<p>Trace les <b>trois hauteurs</b> du triangle ${nA}${nB}${nC} (chacune part d'un `
        + 'sommet et est perpendiculaire au côté opposé). Constate qu\'elles sont concourantes '
        + 'en un point : l\'orthocentre.</p>';
    const payload = makeProgrammeConstruction(
        {
            pxPerCm: PX,
            tolerance: 12,
            viewBox: VBC,
            points: [
                { name: nA, x: A.x, y: A.y }, { name: nB, x: B.x, y: B.y }, { name: nC, x: C.x, y: C.y },
            ],
            preShapes: [
                { type: 'line', from: nB, to: nC },
                { type: 'line', from: nA, to: nB },
                { type: 'line', from: nA, to: nC },
            ],
            tasks: [
                {
                    id: 'hA', kind: 'segment', color: COL_V,
                    consigne: `Trace la hauteur issue de ${nA} (perpendiculaire à (${nB}${nC})).`,
                    shape: { type: 'segment', p1: A, p2: rnd(hA) },
                },
                {
                    id: 'hB', kind: 'segment', color: COL_V,
                    consigne: `Trace la hauteur issue de ${nB} (perpendiculaire à (${nA}${nC})).`,
                    shape: { type: 'segment', p1: B, p2: rnd(hB) },
                },
                {
                    id: 'hC', kind: 'segment', color: COL_V,
                    consigne: `Trace la hauteur issue de ${nC} (perpendiculaire à (${nA}${nB})).`,
                    shape: { type: 'segment', p1: C, p2: rnd(hC) },
                },
            ],
        },
        texte,
        opts(L_TRACER),
    );
    payload._meta.generatorKey = 'tracer_programme_construction';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    point_qcm_unique: genPointQcm,
    position_qcm_unique: genPositionQcm,
    proprietes_tableau_vf: genProprietesVf,
    tracer_programme_construction: genTracerPC,
};

export const CATEGORIES = [
    {
        id: 'orthocentre', label: L_ORTHO, formatsList: [
            { key: 'point_qcm_unique', label: 'QCM nom' },
            { key: 'position_qcm_unique', label: 'QCM position' },
            { key: 'proprietes_tableau_vf', label: 'Vrai/Faux' },
        ],
    },
    {
        id: 'tracer', label: L_TRACER, formatsList: [
            { key: 'tracer_programme_construction', label: 'Construction' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    genPointQcm, genPositionQcm, genProprietesVf, genTracerPC,
};
